import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BoxSizeOptions } from '@styleseat/ui';
import { getUserExistsResponse } from '../../../../../modules/api/Accounts/UserExists';
import type { IRootDispatch, RootState } from '../../../../../store/models';
import { MultiFactorAuthChoiceType, MultiFactorAuthMethod } from '../../../../shared/login/MultiFactorAuthForm/types';
import { InlineSigninAndSignup, LoginStateOptions } from './InlineSigninAndSignup';
import { TERMS_VERSIONS } from '../../../../../modules/Terms';
import { LegalDoc } from '../../../../shared/legal/LegalModal';
import { isValidEmail, isValidPhone } from '../../../../../modules/validators';
import { ModalBox } from '../../../../ui';
import { sendAnalytics } from './utils';
import { AnalyticsEvents, EmailChangeEvent } from './contants';
import analytics from '../../../../../modules/analytics';
import type { AppleSSOResponse } from '../../../../../store/CurrentUser.types';
import { CONTINUE_LOADING_TIMEOUT_MS } from './InlineSigninAndSignupStates/constants';

export type CheckIfUserExistsFunction = {
  (userEmail: string): Promise<{
    doesThisUserExist: boolean;
    hasCheckingError: boolean;
  }>;
};

export type SignInUserFunction = {
  (
    mfaCode?: string,
    currentMfaMethod?: MultiFactorAuthMethod,
    isResend?: boolean,
  ): Promise<{
    mfaRequired: boolean;
    mfaDeliveryOptions: MultiFactorAuthChoiceType[];
    mfaDeliveryMethod: MultiFactorAuthMethod;
    loginError: boolean;
    userData?: any;
  }>;
};

type EmailCheckingState = {
  hasCheckedEmail: boolean;
  hasEmailCheckingError: boolean;
  doesUserExist: boolean;
};

type InlineSigninAndSignupContainerProps = {
  canDisplaySSO?: boolean;
  renderTitle?: () => React.ReactNode;
  renderHeader?: (onNavigateBack: () => void) => React.ReactNode;
  renderFooter?: () => React.ReactNode;
  paddingTop?: BoxSizeOptions;
  paddingBottom?: BoxSizeOptions;
  paddingX?: BoxSizeOptions;
  useInlineMfa?: boolean;
  onContinueAsGuestClick?: () => void;
  onAccountCreated: (success: boolean, userData?: any) => void;
  onSignInSuccess?: (userData: any) => void;
  onChangeState?: (state: LoginStateOptions) => void;
};

export const InlineSigninAndSignupContainer: React
  .FCWithChildren<InlineSigninAndSignupContainerProps> = ({
  canDisplaySSO = false,
  renderTitle = () => null,
  renderHeader = () => null,
  renderFooter = () => null,
  paddingTop = 20,
  paddingBottom = 20,
  paddingX = 0,
  useInlineMfa = false,
  onContinueAsGuestClick,
  onAccountCreated,
  onSignInSuccess,
  onChangeState = () => {},
}) => {
  const dispatch = useDispatch<IRootDispatch>();

  // Selectors
  const userStateErrors = useSelector((state: RootState) => state.user.errors) as any;

  // Local state
  const [componentState, setComponentState] = useState<LoginStateOptions>(
    LoginStateOptions.Initial,
  );
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showLegalModal, setShowLegalModal] = useState<boolean>(false);
  const [showForgotPasswordSuccess, setShowForgotPasswordSuccess] = useState<boolean>(false);
  const [isLoadingPasswordReset, setIsLoadingPasswordReset] = useState<boolean>(false);
  const [emailHasError, setEmailHasError] = useState<boolean>(false);
  const [showPasswordError, setShowPasswordError] = useState<boolean>(true);
  const [phoneNumberError, setPhoneNumberError] = useState<boolean>(false);
  const [showMfa, setShowMfa] = useState<boolean>(false);
  const [emailCheckingState, setEmailCheckingState] = useState<EmailCheckingState>({
    hasCheckedEmail: false,
    hasEmailCheckingError: false,
    doesUserExist: false,
  });
  const [legalDoc, setLegalDoc] = useState<LegalDoc | null>(null);
  const [mfaMethod, setMfaMethod] = useState<MultiFactorAuthMethod | null>(null);
  const [mfaChoices, setMfaChoices] = useState<MultiFactorAuthChoiceType[] | null>(null);

  useEffect(() => {
    const {
      hasCheckedEmail,
      hasEmailCheckingError,
      doesUserExist,
    } = emailCheckingState;

    if (showMfa) {
      setComponentState(LoginStateOptions.MFA);
      return;
    }
    if (!hasCheckedEmail) {
      setComponentState(LoginStateOptions.Initial);
      return;
    }
    if (!hasEmailCheckingError) {
      if (doesUserExist) {
        setComponentState(LoginStateOptions.Login);
        sendAnalytics(AnalyticsEvents.LoginFlowTriggered);
        return;
      }
      setComponentState(LoginStateOptions.SignUp);
      sendAnalytics(AnalyticsEvents.SignupFlowTriggered);
    }
  }, [
    emailCheckingState,
    showMfa,
  ]);

  useEffect(() => {
    onChangeState(componentState);
    if (componentState === LoginStateOptions.Initial) {
      setEmailCheckingState(oldState => ({
        ...oldState,
        hasCheckedEmail: false,
        hasEmailCheckingError: false,
      }));
      setEmailHasError(false);
      setPassword('');
      setShowPasswordError(false);
      setPhoneNumberError(false);
      setIsLoadingPasswordReset(false);
      setShowForgotPasswordSuccess(false);
    }
    if (componentState === LoginStateOptions.Login) {
      setShowMfa(false);
    }
  }, [componentState]);

  // Helper functions
  const clearUserErrors = () => {
    dispatch.user.clearErrors();
  };
  const checkIfUserExists: CheckIfUserExistsFunction = async userEmail => {
    clearUserErrors();
    try {
      const [userExistsAPIResponse] = await Promise.all([
        getUserExistsResponse(userEmail),
        new Promise(resolve => {
          setTimeout(resolve, CONTINUE_LOADING_TIMEOUT_MS);
        }),
      ]);
      return {
        doesThisUserExist: userExistsAPIResponse.user_exists,
        hasCheckingError: false,
      };
    } catch {
      return {
        doesThisUserExist: null,
        hasCheckingError: true,
      };
    }
  };
  const signInUser: SignInUserFunction = async (
    mfaCode,
    currentMfaMethod,
    isResend,
  ) => {
    try {
      const userData: any = await dispatch.user.login({
        email,
        password,
        username: email,
        ...(currentMfaMethod && { mfa_delivery_method: currentMfaMethod }),
        ...(mfaCode && { mfa_code: mfaCode }),
        remember_me: 1,
        is_mfa_resend: isResend ? 1 : 0,
      } as any);
      if (userData.status === 'OK') {
        const {
          mfa_required,
          mfa_delivery_options,
          mfa_delivery_method,
        } = userData;

        return {
          mfaRequired: mfa_required,
          mfaDeliveryOptions: mfa_delivery_options,
          mfaDeliveryMethod: mfa_delivery_method,
          loginError: false,
          userData,
        };
      }
      return {
        mfaRequired: false,
        mfaDeliveryOptions: undefined,
        mfaDeliveryMethod: undefined,
        loginError: true,
      };
    } catch (e) {
      return {
        mfaRequired: false,
        mfaDeliveryOptions: undefined,
        mfaDeliveryMethod: undefined,
        loginError: true,
      };
    }
  };
  const signUpUser = async (): Promise<{
    signupSuccess: boolean;
    userData: any;
  }> => {
    let signupSuccess;
    let userData;
    try {
      userData = await dispatch.user.signupClient({
        firstName,
        lastName,
        email,
        password,
        phone_number: phoneNumber,
        accepted_terms: JSON.stringify([
          {
            accepted_version: TERMS_VERSIONS.eula,
            terms_type: 'eula',
          },
          {
            accepted_version: TERMS_VERSIONS.privacy_policy,
            terms_type: 'privacy_policy',
          },
          {
            accepted_version: TERMS_VERSIONS.consumer_tos,
            terms_type: 'tos',
          },
        ]),
      });
      signupSuccess = userData.status === 'OK';
    } catch {
      signupSuccess = false;
    }
    return {
      signupSuccess,
      userData,
    };
  };

  // Handlers
  const clearAllFields = () => {
    setIsLoading(false);
    setEmail('');
    setEmailHasError(false);
    setPassword('');
    setFirstName('');
    setLastName('');
    setPhoneNumber('');
    setPhoneNumberError(false);
    setShowMfa(false);
    setMfaMethod(null);
    setMfaChoices(null);
    setIsLoadingPasswordReset(false);
    setShowForgotPasswordSuccess(false);
  };
  const handleEmailChange = ({ newValue, validate = true }: EmailChangeEvent) => {
    if (showForgotPasswordSuccess) {
      setShowForgotPasswordSuccess(false);
    }
    setEmail(newValue);
    if (validate) {
      setEmailHasError(!isValidEmail(newValue));
      setEmailCheckingState(oldState => ({
        ...oldState,
        hasCheckedEmail: false,
        hasEmailCheckingError: false,
      }));
    }
    setShowForgotPasswordSuccess(false);
    setIsLoadingPasswordReset(false);
    clearUserErrors();
  };
  const handlePasswordChange = (newPassword: string) => {
    setPassword(newPassword);
    setShowPasswordError(false);
    setShowForgotPasswordSuccess(false);
    setIsLoadingPasswordReset(false);
  };
  const handlePhoneNumberChange = (newPhoneNumber: string) => {
    setPhoneNumber(newPhoneNumber);
    setPhoneNumberError(!isValidPhone(newPhoneNumber));
  };
  const handleFirstNameChange = (newFirstName: string) => setFirstName(newFirstName);
  const handleLastNameChange = (newLastName: string) => setLastName(newLastName);
  const handleCloseMfa = () => setShowMfa(false);
  const showMultiFactorAuth = ({
    mfa_delivery_options: mfaDeliveryOptions,
    mfa_delivery_method: mfaDeliveryMethod,
  }) => {
    setMfaChoices(mfaDeliveryOptions);
    setMfaMethod(mfaDeliveryMethod);
    setShowMfa(true);
  };
  const handleSignInUser = async (
    mfaCode: string,
    currentMfaMethod = mfaMethod,
    isResend: boolean = false,
  ) => {
    setIsLoading(true);
    setShowForgotPasswordSuccess(false);
    setIsLoadingPasswordReset(false);

    const {
      mfaRequired,
      mfaDeliveryOptions,
      mfaDeliveryMethod,
      loginError,
      userData,
    } = await signInUser(
      mfaCode,
      currentMfaMethod,
      isResend,
    );

    if (mfaRequired) {
      showMultiFactorAuth({
        mfa_delivery_options: mfaDeliveryOptions,
        mfa_delivery_method: mfaDeliveryMethod,
      });
    }

    if (!mfaRequired && !loginError) {
      clearAllFields();
      onSignInSuccess?.(userData);
    }

    setIsLoading(false);
  };
  const handleSignUpUser = async () => {
    setIsLoading(true);
    const {
      signupSuccess,
      userData,
    } = await signUpUser();

    if (signupSuccess) {
      clearAllFields();
      onAccountCreated(true, userData);
    } else {
      onAccountCreated(false);
    }
    setIsLoading(false);
  };
  const handleForgotPassword = async (emailToResetPassword: string) => {
    setIsLoadingPasswordReset(true);
    await dispatch.user.forgotPassword({ email: emailToResetPassword });
    if (!emailHasError) {
      setShowForgotPasswordSuccess(true);
    }
    setIsLoadingPasswordReset(false);
  };
  const handleCheckIfUserExists = async (emailValue?: string) => {
    setIsLoading(true);

    const {
      doesThisUserExist,
      hasCheckingError,
    } = await checkIfUserExists(emailValue || email);

    setEmailCheckingState({
      hasCheckedEmail: !hasCheckingError,
      hasEmailCheckingError: hasCheckingError,
      doesUserExist: doesThisUserExist,
    });

    setIsLoading(false);
  };
  const handleGoBack = () => {
    let nextState: LoginStateOptions;
    switch (componentState) {
      case LoginStateOptions.MFA:
        nextState = LoginStateOptions.Login;
        break;
      case LoginStateOptions.Login:
      case LoginStateOptions.SignUp:
      default:
        nextState = LoginStateOptions.Initial;
    }
    setComponentState(nextState);
  };

  const onFacebookSignup = async () => {
    analytics.track('facebook_sso_button_clicked');
    return dispatch.user.loginWithFacebook();
  };

  const onAppleSignup = async (mode: 'signup' | 'login'): Promise<AppleSSOResponse> => {
    analytics.track('apple_sso_button_clicked', { mode });
    return dispatch.user.loginWithApple({
      mode,
    });
  };

  const InlineSigninAndSignupComponent = (
    <InlineSigninAndSignup
      // Components
      renderHeader={renderTitle}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      paddingX={paddingX}
      useInlineMfa={useInlineMfa}
      // Form fields
      email={email}
      firstName={firstName}
      lastName={lastName}
      password={password}
      phoneNumber={phoneNumber}
      // Form update functions
      onEmailChange={handleEmailChange}
      onFirstNameChange={handleFirstNameChange}
      onLastNameChange={handleLastNameChange}
      onPasswordChange={handlePasswordChange}
      onPhoneNumberChange={handlePhoneNumberChange}
      // MFA
      mfaChoices={mfaChoices}
      mfaMethod={mfaMethod}
      onCloseMfa={handleCloseMfa}
      // General state
      canDisplaySSO={canDisplaySSO}
      componentState={componentState}
      isLoading={isLoading}
      isLoadingPasswordReset={isLoadingPasswordReset}
      showForgotPasswordSuccess={showForgotPasswordSuccess}
      showLegalModal={showLegalModal}
      legalDoc={legalDoc}
      hasCheckedEmail={emailCheckingState.hasCheckedEmail}
      setShowLegalModal={setShowLegalModal}
      setLegalDoc={setLegalDoc}
      // Helper functions
      onSignInUser={handleSignInUser}
      onSignUpUser={handleSignUpUser}
      onCheckIfUserExists={handleCheckIfUserExists}
      onForgotPassword={handleForgotPassword}
      onContinueAsGuestClick={onContinueAsGuestClick}
      onFacebookSignup={onFacebookSignup}
      onAppleSignup={onAppleSignup}
      onAccountCreated={onAccountCreated}
      // Errors
      userStateErrors={userStateErrors}
      emailHasError={emailHasError}
      showPasswordError={showPasswordError}
      hasPhoneNumberError={phoneNumberError}
      hasEmailCheckingError={emailCheckingState.hasEmailCheckingError}
    />
  );

  return (
    <React.Fragment>
      {renderHeader(handleGoBack)}
      <ModalBox scrollable padding={0}>
        {InlineSigninAndSignupComponent}
      </ModalBox>
      {renderFooter()}
    </React.Fragment>
  );
};
